/**
 * FeatureHighlightManager - Detects when user looks at features and displays info
 */
import * as THREE from "three"
import { API_URL } from "../config/env.js"

export class FeatureHighlightManager {
  constructor(scene, camera, tourId = null) {
    this.scene = scene
    this.camera = camera
    this.tourId = tourId
    this.features = []
    this.boundingBoxes = new Map() // Map<featureId, THREE.Mesh>
    this.raycaster = new THREE.Raycaster()
    this.raycaster.far = 2 // Limit raycast distance to 2 meters
    this.activeFeature = null
    this.onFeatureChangeCallback = null
    this.updateThrottle = 100 // ms between checks
    this.lastUpdateTime = 0
  }

  /**
   * Load features from API
   */
  async loadFeatures() {
    try {
      // Build API URL with tourId if available
      const url = this.tourId
        ? `${API_URL}/virtual-tour/tours/${this.tourId}/features`
        : `${API_URL}/features`

      const response = await fetch(url)
      this.features = await response.json()

      console.log(`✅ Loaded ${this.features.length} feature highlights`)

      // Create invisible bounding boxes for raycasting
      this.createBoundingBoxes()

      return this.features
    } catch (error) {
      console.error("❌ Error loading features:", error)
      return []
    }
  }

  /**
   * Create invisible bounding box meshes for raycasting
   */
  createBoundingBoxes() {
    // Clear existing
    this.boundingBoxes.forEach((box) => {
      this.scene.remove(box)
      if (box.geometry) box.geometry.dispose()
      if (box.material) box.material.dispose()
    })
    this.boundingBoxes.clear()

    this.features.forEach((feature) => {
      const sizeX = feature.bounds_max_x - feature.bounds_min_x
      const sizeY = feature.bounds_max_y - feature.bounds_min_y
      const sizeZ = feature.bounds_max_z - feature.bounds_min_z

      const centerX = (feature.bounds_min_x + feature.bounds_max_x) / 2
      const centerY = (feature.bounds_min_y + feature.bounds_max_y) / 2
      const centerZ = (feature.bounds_min_z + feature.bounds_max_z) / 2

      const geometry = new THREE.BoxGeometry(sizeX, sizeY, sizeZ)
      const material = new THREE.MeshBasicMaterial({
        visible: false, // Invisible but still raycastable
        transparent: true,
        opacity: 0,
      })

      const box = new THREE.Mesh(geometry, material)
      box.position.set(centerX, centerY, centerZ)
      box.userData.featureId = feature.id
      box.userData.feature = feature

      this.scene.add(box)
      this.boundingBoxes.set(feature.id, box)
    })

    console.log(`📦 Created ${this.boundingBoxes.size} feature bounding boxes`)
  }

  /**
   * Update - check what user is looking at (call every frame, throttled internally)
   */
  update() {
    const now = Date.now()
    if (now - this.lastUpdateTime < this.updateThrottle) {
      return
    }
    this.lastUpdateTime = now

    // Raycast from camera forward
    const direction = new THREE.Vector3()
    this.camera.getWorldDirection(direction)

    this.raycaster.set(this.camera.position, direction)

    // Check intersections with bounding boxes
    const boxesArray = Array.from(this.boundingBoxes.values())
    const intersects = this.raycaster.intersectObjects(boxesArray, false)

    let newFeature = null

    if (intersects.length > 0) {
      // Get closest feature
      newFeature = intersects[0].object.userData.feature
    }

    // Check if feature changed
    if (newFeature?.id !== this.activeFeature?.id) {
      this.activeFeature = newFeature

      if (this.onFeatureChangeCallback) {
        this.onFeatureChangeCallback(newFeature)
      }

      if (newFeature) {
        console.log(`👁️ Looking at: ${newFeature.label}`)
      }
    }
  }

  /**
   * Set callback for when active feature changes
   */
  onFeatureChange(callback) {
    this.onFeatureChangeCallback = callback
  }

  /**
   * Get current active feature
   */
  getActiveFeature() {
    return this.activeFeature
  }

  /**
   * Show debug wireframes (for testing)
   */
  showDebugWireframes(show = true) {
    this.boundingBoxes.forEach((box, featureId) => {
      const feature = this.features.find((f) => f.id === featureId)
      if (!feature) return

      if (show) {
        const edges = new THREE.EdgesGeometry(box.geometry)
        const lineMaterial = new THREE.LineBasicMaterial({
          color: new THREE.Color(feature.highlight_color),
          transparent: true,
          opacity: 0.5,
        })
        const wireframe = new THREE.LineSegments(edges, lineMaterial)
        wireframe.position.copy(box.position)
        wireframe.userData.isDebugWireframe = true
        this.scene.add(wireframe)
        box.userData.debugWireframe = wireframe
      } else {
        if (box.userData.debugWireframe) {
          this.scene.remove(box.userData.debugWireframe)
          box.userData.debugWireframe.geometry.dispose()
          box.userData.debugWireframe.material.dispose()
          delete box.userData.debugWireframe
        }
      }
    })
  }

  /**
   * Cleanup
   */
  dispose() {
    this.boundingBoxes.forEach((box) => {
      this.scene.remove(box)
      if (box.geometry) box.geometry.dispose()
      if (box.material) box.material.dispose()
      if (box.userData.debugWireframe) {
        this.scene.remove(box.userData.debugWireframe)
        box.userData.debugWireframe.geometry.dispose()
        box.userData.debugWireframe.material.dispose()
      }
    })
    this.boundingBoxes.clear()
  }
}
